"use client"

import { updateHome } from '@/features/homeowners/api/homes.api'
import { updateHomeOwner } from '@/features/homeowners/api/homeowners.api'
import { homeFormSchema, HomeFormSchema, UpdateHomeFormSchema, updateHomeFormSchema } from '@/features/homeowners/schema/home/home.schema'
import { Home } from '@/features/homeowners/types/api-response/home-api-response.type'
import { HomeWithHomeOwnerFormType } from '@/features/homeowners/schema/home-with-home-owner.schema'
import { UpdateHomeOwnerFormType, homeOwnerFormSchema } from '@/features/homeowners/schema/home-owner.schema'

// Keys allowed for homeowner PATCH (excludes id, user, createdAt, updatedAt from form state)
const ALLOWED_HOMEOWNER_UPDATE_KEYS = new Set(Object.keys(homeOwnerFormSchema.shape))
import errorToast from '../../../../../shared/lib/toast/error.toast'
import successToast from '../../../../../shared/lib/toast/success.toast'
import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { DefaultValues, FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'
import { DirtyFieldsProvider } from '../../../context/dirty-fields-context'
import StickySaveBar from '../../sticky-save-bar'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { useRegion } from '@/shared/hooks/use-region'
import { useSession } from '@/features/auth/context/session.context'
import { usePathname } from 'next/navigation'
import { buildUrl } from '@/shared/lib/utils'
import { useProjectNotesTagged } from '@/features/homeowners/context/project-notes-tagged-context'
import { useQueryClient } from '@tanstack/react-query'
import { UserRole } from '@/shared/constants/user-roles'
import { ReadOnlyProvider } from '../../../context/read-only-context'
import { transformToE164 } from '@/shared/lib/utils/phone.utils'

type UpdateHomeFormProviderProps = {
    home: Home,
    children: React.ReactNode,
}

function UpdateHomeFormProvider({ home, children }: UpdateHomeFormProviderProps) {
    const [isFormClean, setIsFormClean] = useState(false)
    const [showSuccessBar, setShowSuccessBar] = useState(false)
    const hasInitialized = useRef(false)
    const { region } = useRegionId()
    const { regionSlug } = useRegion()
    const { session, profile } = useSession()
    const queryClient = useQueryClient()
    const pathname = usePathname()
    const { taggedVolunteers, setTaggedVolunteers, noteSource } = useProjectNotesTagged()
    
    // Volunteers, Supporters, and Team Leaders are read-only except for Project General Notes
    const isReadOnly = session?.role === UserRole.VOLUNTEER || session?.role === UserRole.Supporter || session?.role === UserRole.TEAM_LEADER
    const canEditProjectNotes = true // Volunteers and Team Leaders can add new notes
    // Volunteers, Supporters, and Partners cannot toggle Goodwill toggles on Home Details
    const canEditGoodwill = !(
      session?.role === UserRole.VOLUNTEER ||
      session?.role === UserRole.Supporter ||
      session?.role === UserRole.PARTNER
    )
    
    const {
        createdAt,
        updatedAt,
        currentStep,
        isReported,
        homeowner,
        ...homeDetails
    } = home

    const convertNumericFields = useCallback((obj: any, fieldPath: string = ''): any => {
        try {
            if (obj === null || obj === undefined) return obj;
            
            // Handle arrays - preserve them as-is, but ensure interiorWallMaterial is always an array
            if (Array.isArray(obj)) {
                return obj;
            }
            
            // Skip conversion for phone number fields, permit number fields, yes/no fields, and string fields that should remain strings
            if (fieldPath.includes('Phone') || 
                fieldPath.includes('phone') || 
                fieldPath.includes('PermitNumber') ||
                fieldPath.includes('permitNumber') ||
                fieldPath.includes('shedNeeded') ||
                fieldPath.includes('dogsInHome') ||
                fieldPath.includes('hasFloodInsurance') ||
                fieldPath.includes('hasHomeInsurance') ||
                fieldPath.includes('zipCode') ||
                fieldPath === 'homeOwner.dateOfBirth' ||
                fieldPath === 'dateOfBirth') {
                return obj;
            }
            
            // Don't convert dateOfBirth - it must remain a Date object
            if (typeof obj === 'string' && !isNaN(Number(obj)) && obj.trim() !== '' && 
                fieldPath !== 'homeOwner.dateOfBirth' && fieldPath !== 'dateOfBirth') {
                return Number(obj);
            }
            if (typeof obj === 'object' && !Array.isArray(obj)) {
                const converted: any = {};
                for (const [key, value] of Object.entries(obj)) {
                    const currentPath = fieldPath ? `${fieldPath}.${key}` : key;
                    
                    // Handle dateOfBirth conversion from string to Date
                    if (key === 'dateOfBirth' && value) {
                        if (value instanceof Date) {
                            converted[key] = value;
                        } else if (typeof value === 'string') {
                            const dateStr = value.split('T')[0] // Get just the date part if ISO string
                            const [year, month, day] = dateStr.split('-').map(Number)
                            if (year && month && day) {
                                converted[key] = new Date(year, month - 1, day)
                            } else {
                                converted[key] = value; // Keep original if can't parse
                            }
                        } else {
                            converted[key] = value;
                        }
                        continue;
                    }
                    
                    // Keep zipCode as string - don't convert to number
                    if (key === 'zipCode') {
                        converted[key] = value;
                        continue;
                    }
                    
                    // Ensure interiorWallMaterial is always an array
                    if (key === 'interiorWallMaterial') {
                        if (Array.isArray(value)) {
                            converted[key] = value;
                        } else if (value === null || value === undefined || value === '') {
                            converted[key] = [];
                        } else if (typeof value === 'string') {
                            // If it's a string, try to parse it (might be comma-separated or single value)
                            converted[key] = value.includes(',') 
                                ? value.split(',').map((v: string) => v.trim()).filter(Boolean)
                                : [value];
                        } else {
                            converted[key] = [value];
                        }
                        continue;
                    }
                    
                    // Handle claimedBy fields: parse JSON string to array, or convert single string to array
                    if (key.endsWith('ClaimedBy') || key === 'claimedBy') {
                        if (Array.isArray(value)) {
                            converted[key] = value;
                        } else if (value === null || value === undefined || value === '') {
                            converted[key] = [];
                        } else if (typeof value === 'string') {
                            try {
                                // Try to parse as JSON first (for multi-select data stored as JSON)
                                const parsed = JSON.parse(value);
                                if (Array.isArray(parsed)) {
                                    converted[key] = parsed;
                                } else {
                                    // If it's a single value, convert to array
                                    converted[key] = [parsed];
                                }
                            } catch {
                                // If not valid JSON, treat as single string value and convert to array
                                converted[key] = [value];
                            }
                        } else {
                            converted[key] = [value];
                        }
                        continue;
                    }
                    
                    // Handle permit inspection status fields that might have invalid values
                    if (currentPath.includes('permitInspectionStatusInfo')) {
                        // List of valid inspection status values
                        const validStatusValues = ['Needed', 'Cancelled', 'Partial', 'Pass'];
                        
                        // List of inspection status fields that use buildingInspectionStatusOptions
                        const inspectionStatusFields = [
                            'roofingInspections501',
                            'roofingInspections503',
                            'framingInspections105',
                            'insulationInspections107',
                            'plumbingInspections202',
                            'plumbingInspections204',
                            'electricalInspections304',
                            'electricalInspections305',
                            'hvacInspections401',
                            'hvacInspections402',
                            'shutterInspections125',
                            'finalInspection106',
                            'drywallInspections'
                        ];
                        
                        // Check if this is one of the inspection status fields
                        const isInspectionStatusField = inspectionStatusFields.includes(key);
                        
                        if (isInspectionStatusField && typeof value === 'string' && value.trim() !== '' && !validStatusValues.includes(value)) {
                            // Convert invalid enum values to empty string
                            converted[key] = '';
                        } else {
                            converted[key] = convertNumericFields(value, currentPath);
                        }
                    } else {
                        converted[key] = convertNumericFields(value, currentPath);
                    }
                }
                
                // Return the converted object as-is - no special handling for info objects
                // The database structure should match the form structure
                
                return converted;
            }
            return obj;
        } catch (error) {
            console.error(`Error converting numeric fields at path ${fieldPath}:`, error);
            return obj; // Return original object if conversion fails
        }
    }, []);

    // Remove latitude, longitude, and googlePicLink completely from the home details
    // googlePicLink is deprecated - photos are now in S3 and accessible via Photos tab
    const { latitude, longitude, googlePicLink, ...homeDetailsWithoutCoords } = homeDetails;
    
    // Use home.id as a stable key to prevent unnecessary recalculations
    const homeIdRef = useRef(home.id)
    const defaultValues: DefaultValues<HomeWithHomeOwnerFormType> = useMemo(() => {
        const convertedHome = convertNumericFields(homeDetailsWithoutCoords, 'home')
        
        return {
            homeOwner: convertNumericFields(home.homeowner || {}, 'homeOwner'),
            home: convertedHome,
        }
    }, [home.homeowner, homeDetailsWithoutCoords, convertNumericFields])

    // Create a custom schema that excludes latitude/longitude and googlePicLink since they're not user-editable
    // googlePicLink is deprecated - photos are now in S3 and accessible via Photos tab
    const customUpdateHomeSchema = updateHomeFormSchema.omit({
        latitude: true,
        longitude: true,
        googlePicLink: true,
    });

    const form = useForm<HomeWithHomeOwnerFormType>({
        resolver: zodResolver(z.object({ 
            homeOwner: homeOwnerFormSchema.optional(),
            home: customUpdateHomeSchema 
        })),
        defaultValues,
        mode: "onChange",
        shouldFocusError: false,
    })
    
    const { formState: { errors, dirtyFields, isSubmitting, isDirty }, reset, getValues, watch, setValue } = form
    
    // Reset form after initialization to clear any initial dirty state
    // This ensures the form starts clean even if defaultValues transformations cause initial differences
    // With the key prop on the component, this will only run when home.id actually changes
    useEffect(() => {
        // Reset form with current defaultValues to ensure clean state
        // Use keepDefaultValues: true to preserve the defaultValues for comparison
        reset(defaultValues, { 
            keepDefaultValues: true,
            keepDirty: false,
        });
        setIsFormClean(true); // Mark form as clean after reset
        hasInitialized.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [home.id]); // Reset when home.id changes (component remounts with new key)
    
    // Clear clean state when form becomes dirty
    useEffect(() => {
        if (isDirty && isFormClean) {
            setIsFormClean(false);
        }
    }, [isDirty, isFormClean]);
    
    // Also clear clean state when user makes any changes (even if form isn't dirty yet)
    useEffect(() => {
        if (isFormClean) {
            // Add a small delay to prevent immediate clearing after form reset
            const timeoutId = setTimeout(() => {
                // Watch for any form changes to clear the clean state
                const subscription = watch(() => {
                    if (isFormClean) {
                        setIsFormClean(false);
                    }
                });
                return () => subscription.unsubscribe();
            }, 100); // 100ms delay
            
            return () => clearTimeout(timeoutId);
        }
    }, [isFormClean, watch]);
    
    
    // Helper function to normalize claimedBy fields: convert strings to arrays
    const normalizeClaimedByFields = (obj: any): any => {
        if (obj === null || obj === undefined) return obj;
        if (Array.isArray(obj)) return obj;
        
        if (typeof obj === 'object') {
            const normalized: any = {};
            for (const [key, value] of Object.entries(obj)) {
                // Normalize claimedBy fields: convert strings to arrays
                if (key.endsWith('ClaimedBy') || key === 'claimedBy') {
                    if (Array.isArray(value)) {
                        normalized[key] = value;
                    } else if (value === null || value === undefined || value === '') {
                        normalized[key] = [];
                    } else if (typeof value === 'string') {
                        try {
                            // Try to parse as JSON first
                            const parsed = JSON.parse(value);
                            normalized[key] = Array.isArray(parsed) ? parsed : [parsed];
                        } catch {
                            // If not valid JSON, treat as single string value and convert to array
                            normalized[key] = [value];
                        }
                    } else {
                        normalized[key] = [value];
                    }
                } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                    // Recursively process nested objects
                    normalized[key] = normalizeClaimedByFields(value);
                } else {
                    normalized[key] = value;
                }
            }
            return normalized;
        }
        return obj;
    };

    // Helper function to serialize claimedBy arrays to JSON strings and remove id fields
    const serializeClaimedByFields = (obj: any, isTopLevel: boolean = false): any => {
        if (obj === null || obj === undefined) return obj;
        if (Array.isArray(obj)) return obj;
        
        if (typeof obj === 'object') {
            const serialized: any = {};
            for (const [key, value] of Object.entries(obj)) {
                // Skip id fields - they should not be sent in update requests
                // (id is used to identify the record, not to update it)
                if (key === 'id') {
                    continue;
                }
                
                // Serialize claimedBy fields: convert arrays to JSON strings, keep strings as-is
                if (key.endsWith('ClaimedBy') || key === 'claimedBy') {
                    // CRITICAL: Always ensure this is a string, never an array
                    if (Array.isArray(value)) {
                        // Convert array to JSON string for database storage
                        serialized[key] = value.length > 0 ? JSON.stringify(value) : null;
                    } else if (value === null || value === undefined || value === '') {
                        // Keep null/undefined/empty as null
                        serialized[key] = null;
                    } else if (typeof value === 'string') {
                        // If it's already a string (from FormSelectInput), keep it as-is
                        // But if it looks like a JSON array string, ensure it's valid JSON
                        try {
                            const parsed = JSON.parse(value);
                            // If it parses successfully, re-stringify to ensure valid JSON format
                            serialized[key] = JSON.stringify(parsed);
                        } catch {
                            // If not valid JSON, keep as plain string
                            serialized[key] = value;
                        }
                    } else {
                        // For any other type (shouldn't happen, but be safe), convert to string
                        console.warn(`[WARNING] Unexpected type for ${key}:`, typeof value, value);
                        serialized[key] = String(value);
                    }
                    
                    // Final safety check: ensure we never send an array
                    if (Array.isArray(serialized[key])) {
                        console.error(`[ERROR] Serialization failed for ${key}, still an array!`);
                        serialized[key] = JSON.stringify(serialized[key]);
                    }
                } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                    // Recursively process nested objects (not top level, so nested objects can have their own id fields removed)
                    serialized[key] = serializeClaimedByFields(value, false);
                } else {
                    serialized[key] = value;
                }
            }
            return serialized;
        }
        return obj;
    };

    const onSubmit = async (data: HomeWithHomeOwnerFormType) => {
        let lastOperation: 'home' | 'homeOwner' | null = null;
        try {
            // When dirtyFields.home is true (boolean), react-hook-form marks the whole subtree dirty
            // but Object.keys(true) returns [], so we'd miss nested changes like homeStatusInfo.overallStatus.
            // In that case, include all updatable keys from data.home.
            const homeDirtyKeys =
                (dirtyFields.home as unknown) === true
                    ? Object.keys(data.home || {})
                    : Object.keys((dirtyFields.home as Record<string, unknown>) || {});

            // Process home changes - exclude fields that should not be updated
            // These fields are either deprecated, used for identification only, or handled separately by the backend
            const updateHomeData = homeDirtyKeys.reduce((acc, key) => {
                // Skip googlePicLink - it's deprecated and should not be updated
                if (key === 'googlePicLink') {
                    return acc;
                }
                // Skip id - it's used to identify the record, not to update it
                // Including it causes "Invalid relation key: id" errors in the backend
                if (key === 'id') {
                    return acc;
                }
                // Skip regionId - it's not a relation key and not handled directly in updateHomeDetails
                // Including it causes "Invalid relation key: regionId" errors in the backend
                if (key === 'regionId') {
                    return acc;
                }
                // Skip materialsNeeded - it's not mapped to a form field anymore and not in UpdateHomeDto
                // Including it causes "Invalid relation key: materialsNeeded" errors in the backend
                if (key === 'materialsNeeded') {
                    return acc;
                }
                // Type-safe access: ensure key is a string and exists on data.home
                if (key in data.home) {
                    const value = (data.home as any)[key];

                    // Keep rental owner phone behavior consistent with homeowner phone fields:
                    // normalize to E.164 when possible, null when empty.
                    if (key === 'repairabilityInfo' && value && typeof value === 'object') {
                        const nextRepairabilityInfo = { ...value };
                        const rentalOwnerPhone = nextRepairabilityInfo.ownerPhoneNumberIfRental;
                        if (typeof rentalOwnerPhone === 'string') {
                            const trimmedRentalOwnerPhone = rentalOwnerPhone.trim();
                            if (trimmedRentalOwnerPhone === '') {
                                nextRepairabilityInfo.ownerPhoneNumberIfRental = null;
                            } else {
                                const transformedRentalOwnerPhone = transformToE164(trimmedRentalOwnerPhone);
                                if (transformedRentalOwnerPhone && transformedRentalOwnerPhone.trim() !== '') {
                                    nextRepairabilityInfo.ownerPhoneNumberIfRental = transformedRentalOwnerPhone;
                                }
                            }
                        }
                        acc[key] = nextRepairabilityInfo;
                    } else {
                        acc[key] = value;
                    }
                }
                return acc;
            }, {} as Record<string, any>);

            // When saving tagged note notifications, include current project general notes in payload
            if (taggedVolunteers.length > 0 && !('projectGeneralNotes' in updateHomeData)) {
                updateHomeData.projectGeneralNotes = data.home?.projectGeneralNotes;
            }

            // Serialize claimedBy arrays to JSON strings before sending to backend
            // This handles both arrays (from FormMultiSelectInput) and strings (from FormSelectInput)
            // Also removes id fields from nested objects to prevent "Invalid relation key: id" errors
            const serializedUpdateHomeData = serializeClaimedByFields(updateHomeData, true);

            if (taggedVolunteers.length > 0) {
                serializedUpdateHomeData.taggedUserIds = taggedVolunteers.map((v) => v.id);
                serializedUpdateHomeData.actionPath = pathname ? buildUrl(pathname, {}) : undefined;
                if (profile?.fullname) serializedUpdateHomeData.senderName = profile.fullname;
                serializedUpdateHomeData.noteSource = noteSource?.trim() || 'Project General Notes';
            }

            // Same for homeOwner: when dirtyFields.homeOwner is true, include all updatable keys
            const homeOwnerDirtyKeys =
                (dirtyFields.homeOwner as unknown) === true
                    ? Object.keys(data.homeOwner || {}).filter((k) => ALLOWED_HOMEOWNER_UPDATE_KEYS.has(k))
                    : Object.keys((dirtyFields.homeOwner as Record<string, unknown>) || {});

            // Process homeOwner changes: only include keys that are in the update schema (exclude id, user, createdAt, updatedAt)
            const updateHomeOwnerData = homeOwnerDirtyKeys
                .filter((key) => ALLOWED_HOMEOWNER_UPDATE_KEYS.has(key) && key in data.homeOwner)
                .reduce((acc, key) => {
                const value = (data.homeOwner as any)[key]
                
                // Convert dateOfBirth to YYYY-MM-DD string format for backend
                if (key === 'dateOfBirth') {
                    if (value instanceof Date) {
                        const year = value.getFullYear();
                        const month = String(value.getMonth() + 1).padStart(2, '0');
                        const day = String(value.getDate()).padStart(2, '0');
                        acc[key] = `${year}-${month}-${day}`;
                    } else if (value) {
                        acc[key] = value; // Keep as-is if already a string
                    } else {
                        acc[key] = null;
                    }
                } else if (key === 'phone' || key === 'secondaryPhone') {
                    // Transform phone numbers to E.164 format for backend validation
                    if (value && typeof value === 'string' && value.trim() !== '') {
                        const transformed = transformToE164(value);
                        if (transformed && transformed.trim() !== '') {
                            acc[key] = transformed;
                        }
                    } else if (value === null || value === undefined || value === '') {
                        acc[key] = null;
                    }
                } else {
                    acc[key] = value;
                }
                
                return acc;
            }, {} as Record<string, any>);


            if (Object.keys(updateHomeData).length === 0 && Object.keys(updateHomeOwnerData).length === 0 && taggedVolunteers.length === 0) {
                return;
            }

            let hasErrors = false;

            // Update home data if there are changes
            if (Object.keys(serializedUpdateHomeData).length > 0) {
                lastOperation = 'home';
                const homeRes = await updateHome(homeDetails.id, serializedUpdateHomeData)

                if (homeRes == null) {
                    errorToast(
                        `Failed to update home details: Server returned no response (undefined). Home ID: ${homeDetails.id}. Check Network tab for PATCH request.`
                    );
                    hasErrors = true;
                } else if (!homeRes.success) {
                    errorToast(
                        `Failed to update home details: ${homeRes.message ?? 'Unknown error'}. Home ID: ${homeDetails.id}.`
                    );
                    hasErrors = true;
                }
            }

            // Update homeOwner data if there are changes
            if (Object.keys(updateHomeOwnerData).length > 0) {
                if (!home.homeowner?.id) {
                    errorToast("HomeOwner ID not found")
                    hasErrors = true;
                } else {
                    lastOperation = 'homeOwner';
                    const homeOwnerRes = await updateHomeOwner(home.homeowner.id, updateHomeOwnerData as UpdateHomeOwnerFormType)

                    if (homeOwnerRes == null) {
                        errorToast(
                            `Failed to update home owner details: Server returned no response (undefined). Home owner ID: ${home.homeowner.id}. Check Network tab for PUT request.`
                        );
                        hasErrors = true;
                    } else if (!homeOwnerRes.success) {
                        errorToast(
                            `Failed to update home owner details: ${homeOwnerRes.message ?? 'Unknown error'}. Home owner ID: ${home.homeowner.id}.`
                        );
                        hasErrors = true;
                    }
                }
            }

            if (!hasErrors) {
                successToast("Details updated successfully")
                setTaggedVolunteers([])
                // Refetch Homeowners list so recently modified home appears at top
                await queryClient.invalidateQueries({ queryKey: ['homes', 'filter-search'] })
                // Map tab uses lightweight homes (5m stale); invalidate so new lat/lng show immediately
                await queryClient.invalidateQueries({ queryKey: ['homes', 'lightweight'] })
                
                // Mark form as clean by resetting with current values
                const currentValues = getValues();
                
                // Reset the form with current values to clear dirty state
                reset(currentValues);
                
                // Show visual feedback that form was saved
                setIsFormClean(true) // Mark form as clean to disable button
                setShowSuccessBar(true) // Show the sticky success bar
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error("Error in onSubmit (home details update):", {
                error,
                message: errorMessage,
                lastOperationAttempted: lastOperation ?? 'unknown (before first API call)',
                homeId: homeDetails?.id,
                homeOwnerId: home?.homeowner?.id,
                hint: "If message is 'Cannot read properties of undefined (reading success)', the server action returned undefined. Check Network tab for PATCH (home) or PUT (homeowner) and response body.",
            });
            errorToast(
                `Failed to update details: ${errorMessage}. ${lastOperation != null ? `Failed during: ${lastOperation} update. ` : ''}See console for home/home owner IDs and hints.`
            );
        }
    }

    // Hide success bar when form becomes dirty again
    useEffect(() => {
        if (isDirty && showSuccessBar) {
            setShowSuccessBar(false)
        }
    }, [isDirty, showSuccessBar])

    // Handler for sticky bar save button
    const handleStickySave = async () => {
        // Just get the current form data and submit it
        // The onSubmit function already handles validation and dirty field checking
        const data = form.getValues();
        await onSubmit(data);
    }

    return (
        <ReadOnlyProvider isReadOnly={isReadOnly} canEditProjectNotes={canEditProjectNotes} canEditGoodwill={canEditGoodwill}>
            <FormProvider {...form}>
                <DirtyFieldsProvider isFormClean={isFormClean}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className='w-full flex flex-col gap-5 pb-12'>
                        {children}
                    </form>
                    <StickySaveBar 
                        isVisible={isDirty || showSuccessBar}
                        isSuccess={showSuccessBar}
                        isSubmitting={isSubmitting}
                        onSave={handleStickySave}
                    />
                </DirtyFieldsProvider>
            </FormProvider>
        </ReadOnlyProvider>
    )
}

export default UpdateHomeFormProvider
